import { useCallback, useEffect, useMemo, useState } from 'react';
import type { JSX } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { FilterBar, LoadingState } from '@/components';
import { PageHeader, PageLayout, PageContent } from '@/components/blocks/page';
import { Section } from '@/components/blocks/layout';
import { useBreadcrumbEffect, type BreadcrumbItem } from '@/components/blocks/chrome';
import { useCompanies } from '../../hooks';
import { useAllReports } from '../../../reports/hooks';
import { createSummaries, soonThreshold } from './utils';
import type { CompanySummary } from './types';
import { NewCompanyModal } from './NewCompanyModal';
import { NavigableEntityCard, Panel } from '@/components/blocks/data-display';
import { CompanyLogoMark } from '../../components/CompanyLogoMark';
import { useDebouncedCallback } from '@/utils/debounce';
import { trackEvent } from '@/lib/analytics';
import { DataRecoveryAlertConnected, EmptyState } from '@/components/blocks/feedback';

const COMPANY_FILTERS = [
  {
    id: 'overdue',
    label: 'Audit in ritardo',
    predicate: (summary: CompanySummary) => summary.overdue,
  },
  {
    id: 'due-soon',
    label: 'Audit imminenti',
    predicate: (summary: CompanySummary) => summary.dueSoon && !summary.overdue,
  },
  {
    id: 'nc-open',
    label: 'NC aperte',
    predicate: (summary: CompanySummary) => summary.ncOpen > 0,
  },
] as const;

type CompanyFilterId = (typeof COMPANY_FILTERS)[number]['id'];

const FILTERS_BY_ID = COMPANY_FILTERS.reduce(
  (acc, filter) => acc.set(filter.id, filter),
  new Map<CompanyFilterId, (typeof COMPANY_FILTERS)[number]>()
);

export function CompaniesListScreen(): JSX.Element {
  const navigate = useNavigate({ from: '/companies' });
  const {
    data: companiesData,
    isLoading: loadingCompanies,
    isError: companiesError,
    error: companiesErrorData,
  } = useCompanies();
  const {
    data: reportsData,
    isLoading: loadingReports,
    isError: reportsError,
    error: reportsErrorData,
  } = useAllReports();
  const [newCompanyOpen, setNewCompanyOpen] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [activeFilters, setActiveFilters] = useState<CompanyFilterId[]>([]);

  const [now] = useState(() => Date.now());
  const threshold = soonThreshold(now);

  const summaries = useMemo(
    () => createSummaries(companiesData, reportsData ?? [], now, threshold),
    [companiesData, reportsData, now, threshold]
  );

  const filterOptions = useMemo(
    () =>
      COMPANY_FILTERS.map((filter) => ({
        id: filter.id,
        label: filter.label,
        count: summaries.filter(filter.predicate).length,
      })),
    [summaries]
  );

  const debouncedSearch = useDebouncedCallback((value: unknown) => {
    if (typeof value !== 'string') return;
    setSearchQuery(value.trim().toLowerCase());
  }, 150);

  const handleSearchChange = useCallback(
    (value: string) => {
      setSearchInput(value);
      debouncedSearch.run(value);
    },
    [debouncedSearch]
  );

  const handleFilterToggle = useCallback((filterId: string) => {
    setActiveFilters((prev) => {
      const typedId = filterId as CompanyFilterId;
      return prev.includes(typedId) ? prev.filter((id) => id !== typedId) : [...prev, typedId];
    });
  }, []);

  const handleClearFilters = useCallback(() => {
    setActiveFilters([]);
  }, []);

  const isLoading = loadingCompanies || loadingReports;
  const isError = companiesError || reportsError;
  const dataLoadError = companiesErrorData ?? reportsErrorData ?? null;

  const normalizedSearch = searchQuery.trim();

  const filteredSummaries = useMemo(() => {
    const searchTerm = normalizedSearch;
    return summaries.filter((summary) => {
      const matchesSearch =
        searchTerm.length === 0 ||
        summary.name.toLowerCase().includes(searchTerm) ||
        (summary.code ?? '').toLowerCase().includes(searchTerm);

      if (!matchesSearch) return false;

      if (activeFilters.length === 0) return true;

      return activeFilters.some((filterId) => {
        const filter = FILTERS_BY_ID.get(filterId);
        return filter ? filter.predicate(summary) : false;
      });
    });
  }, [summaries, normalizedSearch, activeFilters]);

  useEffect(() => {
    if (normalizedSearch.length === 0) return;
    if (isLoading || isError) return;
    trackEvent('clients.search', {
      query: normalizedSearch,
      filters: activeFilters,
      totalMatches: filteredSummaries.length,
    });
  }, [activeFilters, filteredSummaries.length, isError, isLoading, normalizedSearch]);

  const breadcrumbFactory = useCallback(
    (baseTrail: BreadcrumbItem[]) => [...baseTrail, { label: 'Aziende', href: '/companies' }],
    []
  );

  useBreadcrumbEffect(breadcrumbFactory);

  const handleOpen = (companyId: string) => {
    navigate({
      to: '/companies/$companyId',
      params: { companyId },
      search: { tab: undefined },
    });
  };

  const handleCreated = (companyId: string) => {
    setNewCompanyOpen(false);
    handleOpen(companyId);
  };

  return (
    <PageLayout>
      <PageHeader
        title="Aziende"
        icon="corporate_fare"
        primaryAction={{
          label: 'Nuova azienda',
          onClick: () => setNewCompanyOpen(true),
        }}
      />

      <PageContent>
        <Section>
          <FilterBar
            searchQuery={searchInput}
            onSearchChange={handleSearchChange}
            searchPlaceholder="Cerca per nome o codice"
            filters={filterOptions}
            activeFilters={activeFilters}
            onFilterToggle={handleFilterToggle}
            onClearFilters={handleClearFilters}
          />

          {isLoading ? (
            <Panel>
              <LoadingState label="Carichiamo i clienti…" />
            </Panel>
          ) : isError ? (
            <DataRecoveryAlertConnected
              title="Impossibile caricare le aziende"
              error={dataLoadError}
            />
          ) : summaries.length === 0 ? (
            <Panel>
              <EmptyState
                icon="corporate_fare"
                title="Nessuna azienda registrata"
                description="Crea la prima azienda per iniziare a monitorare audit e sopralluoghi."
                action={{
                  label: 'Nuova azienda',
                  onClick: () => setNewCompanyOpen(true),
                }}
                variant="inline"
              />
            </Panel>
          ) : filteredSummaries.length === 0 ? (
            <Panel>
              <EmptyState
                icon="search"
                title="Nessuna azienda trovata"
                description="Aggiorna la ricerca o ripristina i filtri per vedere la lista completa."
                action={
                  activeFilters.length > 0
                    ? {
                        label: 'Pulisci filtri',
                        onClick: handleClearFilters,
                      }
                    : undefined
                }
                variant="inline"
              />
            </Panel>
          ) : (
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {filteredSummaries.map((summary) => (
                <CompanyCard key={summary.id} summary={summary} onOpen={handleOpen} />
              ))}
            </div>
          )}
        </Section>
      </PageContent>

      <NewCompanyModal
        open={newCompanyOpen}
        onOpenChange={setNewCompanyOpen}
        onCreated={handleCreated}
      />
    </PageLayout>
  );
}

interface CompanyCardProps {
  summary: CompanySummary;
  onOpen: (companyId: string) => void;
}

const metricNumberFormatter = new Intl.NumberFormat('it-IT');

const formatMetricValue = (value: number): string => metricNumberFormatter.format(value);

const resolveSitesLabel = (count: number): string =>
  count === 1 ? 'Stabilimento:' : 'Stabilimenti:';

const resolveNcLabel = (count: number): string => (count === 1 ? 'NC aperta:' : 'NC aperte:');

function CompanyCard({ summary, onOpen }: CompanyCardProps) {
  const nextVisitTone = summary.overdue
    ? 'danger'
    : summary.dueSoon
      ? 'warning'
      : summary.nextVisitAt
        ? 'info'
        : 'neutral';
  const nextVisitLabel = summary.nextVisitLabel;

  // Combine sites + units into single row
  const sitesUnitsLabel = `${resolveSitesLabel(summary.sitesCount)} ${formatMetricValue(summary.sitesCount)} · Impianti: ${formatMetricValue(summary.unitsCount)}`;

  const isPlanned = summary.nextVisitAt !== null && summary.nextVisitAt !== undefined;

  return (
    <NavigableEntityCard
      title={summary.name}
      icon="domain"
      headerAside={<CompanyLogoMark logoUrl={summary.logoUrl} size="md" />}
      id={summary.code ?? 'N/D'}
      idLabel={summary.code ? 'Codice' : null}
      rows={[
        {
          label: 'Prossimo audit:',
          value: isPlanned ? (
            nextVisitLabel
          ) : (
            <span className="font-sans text-sm font-normal text-text-muted">{nextVisitLabel}</span>
          ),
          tone: nextVisitTone,
        },
        {
          label: resolveNcLabel(summary.ncOpen),
          value: formatMetricValue(summary.ncOpen),
          tone: summary.ncOpen > 0 ? 'danger' : 'neutral',
        },
      ]}
      onClick={() => onOpen(summary.id)}
    >
      <p className="text-xs text-text-secondary">{sitesUnitsLabel}</p>
    </NavigableEntityCard>
  );
}
